import { useState } from "react";
import { toast } from "sonner";
import { BellRinging, MoonStars } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NUDGE_GROUPS = [
  ["checkin", "Check-in reminders", "Nudges to plan your optimal day"],
  ["recovery_sleep", "Recovery & sleep alerts", "When recovery or sleep declines"],
  ["stress", "Stress alerts", "When your stress load runs high"],
  ["motivation", "Motivation & trends", "Consistency and trajectory insights"],
  ["celebrations", "Celebrations", "Streaks, milestones and wins"],
];

export default function NotificationPrefs() {
  const { user, checkAuth } = useAuth();
  const initial = user?.notification_prefs || {};
  const [prefs, setPrefs] = useState({
    push_morning: initial.push_morning ?? true,
    push_evening: initial.push_evening ?? true,
    push_weekly: initial.push_weekly ?? true,
    push_anomaly: initial.push_anomaly ?? true,
    push_comeback: initial.push_comeback ?? true,
    quiet_enabled: initial.quiet_enabled ?? false,
    quiet_start: initial.quiet_start || "22:00",
    quiet_end: initial.quiet_end || "07:00",
    nudges: {
      checkin: true, recovery_sleep: true, stress: true, motivation: true, celebrations: true,
      ...(initial.nudges || {}),
    },
  });
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setPrefs((p) => ({ ...p, [key]: value }));
  const setNudge = (key, value) => setPrefs((p) => ({ ...p, nudges: { ...p.nudges, [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/preferences", {
        notification_prefs: { ...prefs, tz_offset_minutes: new Date().getTimezoneOffset() },
      });
      toast.success("Notification preferences saved");
      await checkAuth();
    } catch {
      toast.error("Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="clay p-6 sm:p-8" data-testid="notification-prefs-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center">
          <BellRinging size={20} weight="fill" color="var(--brand)" />
        </div>
        <div>
          <h3 className="font-display text-lg font-medium">Notifications</h3>
          <p className="text-xs text-muted2">Choose which nudges reach you, and when.</p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <div className="label-eyebrow">Push notifications</div>
        <PrefRow
          testId="pref-push-morning"
          title="Morning check-in reminder"
          desc="A gentle prompt to plan your day"
          checked={prefs.push_morning}
          onChange={(v) => set("push_morning", v)}
        />
        <PrefRow
          testId="pref-push-evening"
          title="Evening wind-down"
          desc="A reminder to protect your sleep"
          checked={prefs.push_evening}
          onChange={(v) => set("push_evening", v)}
        />
        <PrefRow
          testId="pref-push-weekly"
          title="Weekly review (Sunday evening)"
          desc="Your week's stats + coach summary, every Sunday at 6pm"
          checked={prefs.push_weekly}
          onChange={(v) => set("push_weekly", v)}
        />

        <PrefRow
          testId="pref-push-anomaly"
          title="Anomaly alerts (morning)"
          desc="When wearable data deviates from your 2-week baseline — e.g. elevated resting HR"
          checked={prefs.push_anomaly}
          onChange={(v) => set("push_anomaly", v)}
        />

        <PrefRow
          testId="pref-push-comeback"
          title="Comeback nudges"
          desc="A gentle hello if you've been away 3, 7 or 14 days — never guilt"
          checked={prefs.push_comeback}
          onChange={(v) => set("push_comeback", v)}
        />

        <div className="rounded-2xl border border-border p-4" data-testid="quiet-hours-block">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <MoonStars size={18} weight="fill" color="var(--brand)" />
              <div>
                <div className="text-sm font-medium">Quiet hours</div>
                <div className="text-xs text-muted2">No pushes during this window</div>
              </div>
            </div>
            <Switch data-testid="pref-quiet-enabled" checked={prefs.quiet_enabled} onCheckedChange={(v) => set("quiet_enabled", v)} />
          </div>
          {prefs.quiet_enabled && (
            <div className="flex items-center gap-3 mt-4">
              <label className="text-xs text-muted2">From</label>
              <input
                type="time" data-testid="quiet-start-input" value={prefs.quiet_start}
                onChange={(e) => set("quiet_start", e.target.value)}
                className="bg-surface border border-border rounded-xl px-3 py-1.5 text-sm"
              />
              <label className="text-xs text-muted2">to</label>
              <input
                type="time" data-testid="quiet-end-input" value={prefs.quiet_end}
                onChange={(e) => set("quiet_end", e.target.value)}
                className="bg-surface border border-border rounded-xl px-3 py-1.5 text-sm"
              />
            </div>
          )}
        </div>

        <div className="label-eyebrow pt-2">Coach nudges</div>
        {NUDGE_GROUPS.map(([key, title, desc]) => (
          <PrefRow
            key={key}
            testId={`pref-nudge-${key}`}
            title={title}
            desc={desc}
            checked={prefs.nudges[key]}
            onChange={(v) => setNudge(key, v)}
          />
        ))}
      </div>

      <button
        data-testid="save-notification-prefs-btn"
        onClick={save}
        disabled={saving}
        className="mt-6 bg-ink text-bg rounded-full px-6 py-2.5 text-sm font-medium hover:bg-brand transition-colors disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save preferences"}
      </button>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted2">{desc}</div>
      </div>
      <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
